import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { IconDownload, IconExternalLink, IconRefresh } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import { openPrivateFile } from "./api";

// How long a preview may take before the viewer says it didn't load.
export const PREVIEW_TIMEOUT_MS = 10000;

export const fileName = (file) =>
  String(file || "")
    .split("/")
    .pop();

// What the viewer can show for a file type: an image inline, a PDF in the
// browser's own viewer, anything else only as a download.
export function previewKind(type) {
  const t = String(type || "").toLowerCase();
  // HEIC/HEIF: most browsers can't draw them; the img fails and the viewer
  // offers the download.
  if (t.startsWith("image/")) return "image";
  if (t === "application/pdf") return "pdf";
  return "other";
}

// Whether a document may be approved from the viewer, and if not, the words
// shown next to the disabled button. The agent must have seen it: the image
// drew, the PDF opened, or they downloaded the file.
export function viewerGate({ shown, downloaded, failed, kind }) {
  if (shown || downloaded) return { canApprove: true, why: "" };
  if (failed || kind === "other") return { canApprove: false, why: "Download the file and check it before approving." };
  return { canApprove: false, why: "You can approve once the document is showing." };
}

// Fetches a private file for the viewer. `preloaded` (the row's thumbnail) is
// used only if it is there when the viewer opens: a thumbnail that arrives
// later never cancels the viewer's own load (that race left the passport on
// an endless spinner). Every blob URL made here is released here. `open`
// fetches the file (a customer's private file unless told otherwise).
export function useFilePreview(file, preloaded, open = openPrivateFile) {
  const opener = useRef(open);
  opener.current = open;
  const initial = useRef(preloaded);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(() =>
    preloaded ? { phase: "ready", url: preloaded.url, type: preloaded.type } : { phase: "loading", url: null, type: "" },
  );
  const [shown, setShown] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setShown(false);
    setTimedOut(false);
    const reuse = attempt === 0 && initial.current;
    if (reuse) {
      setState({ phase: "ready", url: initial.current.url, type: initial.current.type });
      return undefined;
    }
    let alive = true;
    let url = null;
    setState({ phase: "loading", url: null, type: "" });
    opener
      .current(file)
      .then((result) => {
        if (!alive) {
          URL.revokeObjectURL(result.url);
          return;
        }
        url = result.url;
        setState({ phase: "ready", url: result.url, type: result.type });
      })
      .catch(() => alive && setState({ phase: "failed", url: null, type: "" }));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file, attempt]);

  // The watchdog: not on screen after 10 s means it didn't load. A preview
  // that turns up later still replaces the message.
  useEffect(() => {
    if (shown) return undefined;
    const timer = setTimeout(() => setTimedOut(true), PREVIEW_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [shown, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const onShown = useCallback(() => setShown(true), []);
  const onBroken = useCallback(() => setState((s) => ({ ...s, phase: "failed" })), []);

  const kind = state.url ? previewKind(state.type) : null;
  const failed = state.phase === "failed" || (timedOut && !shown) || kind === "other";
  return { ...state, kind, shown, failed, timedOut, retry, onShown, onBroken };
}

// A file name without an extension gets one from its type, so a download
// opens on the person's computer ("receipt-12" -> "receipt-12.pdf").
const EXTENSIONS = { "application/pdf": "pdf", "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/heic": "heic", "image/heif": "heif" };
export function downloadName(file, type) {
  const name = fileName(file);
  const ext = EXTENSIONS[String(type || "").toLowerCase()];
  return /\.[a-z0-9]{2,5}$/i.test(name) || !ext ? name : `${name}.${ext}`;
}

// Download is always offered: from the loaded copy when there is one,
// otherwise fetched on the press (so it works when the preview failed).
export function DownloadButton({ file, url, type, open = openPrivateFile, onDownloaded }) {
  const [error, setError] = useState(false);
  const save = (href, kind) => {
    const a = document.createElement("a");
    a.href = href;
    a.download = downloadName(file, kind);
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const onClick = async () => {
    setError(false);
    if (url) {
      save(url, type);
      onDownloaded?.();
      return;
    }
    try {
      const result = await open(file);
      save(result.url, result.type);
      setTimeout(() => URL.revokeObjectURL(result.url), 30000);
      onDownloaded?.();
    } catch {
      setError(true);
    }
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <ActionButton variant="text" onClick={onClick} startIcon={<IconDownload size={16} />}>
        Download
      </ActionButton>
      {error && (
        <Typography variant="body2" color="error.main" role="alert">
          The download didn’t start. Try again.
        </Typography>
      )}
    </Box>
  );
}

DownloadButton.propTypes = { file: PropTypes.string.isRequired, url: PropTypes.string, type: PropTypes.string, open: PropTypes.func, onDownloaded: PropTypes.func };

// The large preview: a spinner with words while it loads, the document once
// it has, and after 10 s (or a broken file) an honest message with Download
// and Try again.
export function FilePreviewPane({ file, title, preview, onDownloaded }) {
  const { url, kind, shown, failed, retry, onShown, onBroken } = preview;
  const pdfOpen = () => {
    window.open(url, "_blank", "noopener");
    onDownloaded?.();
  };
  return (
    <Box sx={{ position: "relative", width: "100%", minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {!failed && !shown && (
        <Box textAlign="center" role="status" sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={32} aria-hidden />
          <Typography variant="body2" color="text.secondary" mt={1}>
            Opening the document…
          </Typography>
        </Box>
      )}
      {failed && (
        <Alert
          severity={kind === "other" ? "info" : "warning"}
          role="alert"
          sx={{ alignItems: "center", maxWidth: 520, position: "relative", zIndex: 1 }}
          action={
            kind !== "other" && (
              <ActionButton onClick={retry} startIcon={<IconRefresh size={16} />}>
                Try again
              </ActionButton>
            )
          }
        >
          {/* Download sits in the viewer's footer, always there. */}
          {kind === "other" ? "This kind of file can’t be shown here. Download it to check it." : "The preview didn’t load. Download the file, or try again."}
        </Alert>
      )}
      {url && kind === "image" && (
        <img
          src={url}
          alt={title}
          onLoad={onShown}
          onError={onBroken}
          style={{ maxWidth: "100%", maxHeight: "72vh", objectFit: "contain", display: failed ? "none" : "block", visibility: shown ? "visible" : "hidden" }}
        />
      )}
      {url && kind === "pdf" && (
        <Box sx={{ width: "100%", display: failed ? "none" : "block" }}>
          <iframe title={title} src={url} onLoad={onShown} style={{ width: "100%", height: "68vh", border: 0, display: "block" }} />
          <Box sx={{ textAlign: "right", pt: 1 }}>
            <ActionButton variant="text" onClick={pdfOpen} startIcon={<IconExternalLink size={16} />}>
              Open in a new tab
            </ActionButton>
          </Box>
        </Box>
      )}
    </Box>
  );
}

FilePreviewPane.propTypes = { file: PropTypes.string.isRequired, title: PropTypes.string, preview: PropTypes.object.isRequired, onDownloaded: PropTypes.func };
